/**
 * cypAdhdSsiContent.js — the clinician-facing content for each SSI symptom.
 *
 * Transcribed from Jasmine's "CYP ADHD SSI (5 to 16 years)" content doc. Per
 * symptom: a clinical tooltip, the suggested interview prompts, and two
 * checklists of example manifestations — one for home and one for education,
 * shown side by side. Home and Education are setting chips, not a Yes/No
 * on each list (October 2026 revision of the doc; the single flat checklist
 * and the separate settings checklist it replaced were retired with it).
 *
 * THIS FILE IS THE SINGLE PLACE THIS CONTENT IS AUTHORED. The Python half of
 * the contract (constants/cyp_adhd/ssi_content.py) is GENERATED from this file
 * by scripts/generateSsiContentPython.js — do not hand-edit the Python. Two
 * hand-maintained copies of ~200 strings containing apostrophes ("doesn't
 * notice", "always on the go") is how a quoting error gets into a file that
 * every CYP cloud function imports.
 *
 * Content notes, so the transcription is auditable against the doc:
 *  - Tooltips and prompts are unchanged by the October revision and carried
 *    over as they were transcribed before.
 *  - Curly quotes and non-breaking hyphens are normalised to plain ones;
 *    WORDING is otherwise verbatim except for four grammatical slips
 *    ("Teachers comments that" -> "Teachers comment that", "Often need to
 *    check" -> "Often needs to check", "immediately expressing them" ->
 *    "immediately expresses it", "disrupts peers and disruptive to" ->
 *    "disrupts peers and is disruptive to") and "masking" -> "camouflaging" in four
 *    tooltips, per the design rules' clinical-language table for children.
 *    Both flagged for Tom's review.
 *  - Two mechanical fixes: A1d's "for for incomplete work" loses the repeated
 *    word, and A2i's home list drops a fragment ("Behaviour seen as
 *    attention-seeking") fused onto the end of another line in the doc, which
 *    repeats the list's own "Seen as attention-seeking" item.
 *  - Trailing full stops are dropped: these render as checkbox labels.
 *  - The asterisked A1b examples keep their asterisk; examplesFootnote carries
 *    the doc's explanation of it. A1d's "don't include difficulties due to not
 *    understanding or deliberately disobeying" sits in that symptom's tooltip
 *    (the info icon beside the heading), not as a line above the checklists.
 *    examplesHint is '' on every symptom.
 *  - The doc's "Other:" line is NOT an example. The panel no longer collects
 *    it; anything extra is written in the symptom's clinical notes.
 *  - The doc's "Current / Primary school" ticks are not shown. Anything
 *    already stored stays on the stage document.
 *  - The reframing tables ("For the AI Agent") are not wired in: there is no
 *    report section for strengths or adjustments yet, and the A2 tables are
 *    empty in the doc.
 *
 * Keyed by DSM code so a symptom's content cannot be attached to the wrong
 * symptom by an ordering mistake.
 */

export const CYP_SSI_CONTENT = {
  A1a: {
    tooltip: 'Consider a "Knowledge-Performance Gap": high "careless mistakes" despite strong understanding. If errors reflect gaps in knowledge, consider learning needs. If errors reflect oversight, missed details, or mental drift, consider inattention. Differentiate inability to perform the task from inconsistent execution of a task they are capable of doing.',
    suggestedPrompts: [
      'Can you tell me about situations where they understand what to do, but still make mistakes, miss details, or overlook parts of the task?',
      'What kinds of mistakes happen most often?',
    ],
    homeExamples: [
      'Misses steps in chores or routines, so they need redoing',
      'Breaks or spills things by rushing without looking',
      'Makes mistakes in homework despite understanding it',
      'Doesn\'t notice hazards such as traffic when crossing roads or hot surfaces',
      'Needs a lot of time to complete detailed tasks',
    ],
    educationExamples: [
      'Misreads or misses details in instructions, leading to errors',
      'Skips questions, or doesn\'t turn over the page on tests',
      'Hands in rushed or incomplete work with avoidable errors',
      'Rarely checks work before handing it in',
      'Finds detail-heavy tasks especially stressful or time-consuming',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A1b: {
    tooltip: 'It is important to consider the "Hyperfocus Exception." Children and adolescents with ADHD may sustain prolonged concentration during activities that are highly stimulating, emotionally rewarding, novel, personally interesting (e.g., gaming, social media, creative interests, preferred hobbies, special interests). This does not rule out the symptom. Assess sustained attention during effortful, low-stimulation tasks.',
    suggestedPrompts: [
      'Can you describe what tends to happen to their attention during activities that require sustained focus or concentration?',
      'What happens when the task is long, repetitive, or less interesting?',
    ],
    homeExamples: [
      'Frequently switches toys or activities, at home or at clubs',
      'Only stays with a task or game for a few minutes*',
      'Struggles to sit through a book or film*',
      'Loses their train of thought in conversations',
      'Needs structure to avoid becoming distracted',
    ],
    educationExamples: [
      'Attention starts well but fades as the task goes on*',
      'Struggles to keep focus on long or repetitive tasks (e.g., essays)*',
      'Says tasks are boring and loses focus before the end*',
      'Keeps switching games or playmates at break and lunch',
      'Needs frequent reminders and redirection to stay on task',
    ],
    examplesHint: '',
    examplesFootnote: '*Less so when highly interested in the activity or subject',
  },
  A1c: {
    tooltip: 'If they "do not listen" only when there is background noise, consider sensory/auditory processing. This symptom highlights moments where the mind seems elsewhere even without obvious distraction. The difficulty is often not unwillingness to listen, but inconsistent attentional engagement.',
    suggestedPrompts: [
      'Can you tell me about situations where someone is speaking directly to them, but they do not seem to have taken in what was said?',
      'Do they seem aware they missed what was said?',
    ],
    homeExamples: [
      'Seems to be daydreaming or mind elsewhere, even when spoken to directly',
      'Family or visitors comment that they don\'t listen or seem to "zone out"',
      'Needs to be called several times or have questions repeated',
      'Needs strong cues (e.g., eye contact, louder voice, touch) to register what is said',
      'Seems to listen but can\'t repeat back what was just said (e.g., instructions)',
    ],
    educationExamples: [
      'Teachers comment that they don\'t listen in class',
      'Teachers comment that they require multiple prompts',
      'Teachers think that they are being defiant by not following instructions',
      'Needs instructions repeated or broken into small steps',
      'Seated at the front or next to an assistant to help them listen',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A1d: {
    tooltip: 'If Low Compliance is flagged, differentiate: oppositional behaviour, misunderstanding of instructions, or inattention (e.g. forgetting steps, losing track, drifting off). This symptom reflects intended follow-through but inconsistent execution due to an inability to sustain effort through to completion, not a lack of willingness. Don\'t include difficulties due to not understanding or deliberately disobeying.',
    suggestedPrompts: [
      'Can you tell me about situations where they start tasks, responsibilities, or plans but struggle to carry them through to completion, even when they intend to?',
      'What usually gets in the way of completing these tasks?',
    ],
    homeExamples: [
      'Leaves chores half-finished or skips parts of them',
      'Doesn\'t finish self-care routines (e.g., gets partly dressed, then drifts off)',
      'Starts building or making things but leaves them unfinished',
      'Needs many reminders to finish an activity',
      'Needs a lot of structure in order to complete tasks',
    ],
    educationExamples: [
      'Difficulty following or remembering instructions, leading to incomplete tasks',
      'Begins tasks but misses out steps or goes off tasks and leaves it incomplete',
      'Requires structure and direction to complete tasks',
      'Gets warnings or detentions for incomplete work, misperceived as defiance',
      'Needs a time limit to complete tasks',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A1e: {
    tooltip: 'Differentiate between Physical Disorganisation (messy room, bag or folders) and Temporal Disorganisation (difficulty sequencing, planning, prioritising, or managing time). Some children and adolescents may appear organised due to external structure providing scaffolding, but they remain internally disorganised. Focus on their internal ability to plan, manage time and sequences when external supports are removed.',
    suggestedPrompts: [
      'When they need to organise or plan tasks - like homework, getting ready, projects, or daily routines - can you describe how they go about it?',
      'What happens when they have multiple things to manage?',
    ],
    homeExamples: [
      'Often seems rushed or is late for clubs, activities or appointments',
      'Does tasks in an illogical or haphazard order because of difficulty planning',
      'Has difficulty keeping belongings, toys or clothes tidy and organised',
      'Misjudges how long tasks or activities will take',
      'Has difficulty balancing homework, leisure activities and other responsibilities',
    ],
    educationExamples: [
      'Misses deadlines or hands work in late due to poor planning/organisation',
      'Belongings, bag or appearance are messy and disorganised',
      'Notes taken in lessons lack structure and are hard to use later',
      'Frequently stressed or late with more autonomy (e.g. secondary transition)',
      'Unable to use planners, timetables or schedules consistently',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A1f: {
    tooltip: 'This symptom is the primary driver of "Task Initiation" difficulties. Differentiate: "I do not want to do this" may indicate oppositionality (ODD); "This feels mentally painful to start" is more consistent with ADHD. If they eventually complete the task after a long activation delay, the symptom could be ADHD-related. If they refuse regardless of effort, consider ODD boundaries.',
    suggestedPrompts: [
      'Can you tell me what usually happens when they need to start or keep going with tasks that take a lot of concentration or mental effort?',
      'What kinds of tasks are hardest to get started on?',
    ],
    homeExamples: [
      'Procrastinates on homework or mentally demanding chores',
      'Does not persevere with tasks that are hard or repetitive',
      'Starts with easy or enjoyable parts and puts off more effortful steps',
      'Disengages from activities or games that lack stimulation or feel long or repetitive',
      'Needs repeated prompting, or only does homework after long battles',
    ],
    educationExamples: [
      'Avoids lessons requiring sustained effort (e.g., maths, writing, essays, projects)',
      'Protests, becomes frustrated, or shows low effort during demanding lessons',
      'Becomes restless/oppositional or makes excuses to leave class',
      'Says they feel ill, or truants, to avoid lessons they find intensive',
      'Dislikes reading due to mental effort required',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A1g: {
    tooltip: 'While most children and adolescents lose things occasionally, ADHD-related loss is chronic and disruptive. If parents have created a scaffolding system (e.g., everything labelled, clipped, duplicated), the symptom can still be present but the scaffolding is compensating for the underlying difficulty. Focus on their unaided ability to keep track of items.',
    suggestedPrompts: [
      'When they are moving between school, home, or activities, can you describe what usually happens with their belongings, materials, or things they need?',
      'What kinds of things tend to go missing?',
    ],
    homeExamples: [
      'Regularly loses or misplaces belongings such as keys, clothing, or toys',
      'Has no idea where or when they last had the missing or lost item',
      'Turns up to activities without equipment they need (e.g., coat, football, racket)',
      'Often leaves things behind',
      'Gets in a panic if people move their personal items around',
    ],
    educationExamples: [
      'Frequently loses or misplaces school items (e.g., bag, books, uniform, sports kit)',
      'Often needs to check lost property for clothing, pencil cases or books',
      'Attends lessons without the equipment or materials required',
      'Leaves belongings behind in classrooms or around school',
      'Spends lesson time searching for misplaced items instead of working',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A1h: {
    tooltip: 'This symptom is highly sensitive to the environment. If they can focus in a "blacked-out" room with noise-cancelling headphones but are "distracted" everywhere else, the symptom can still be positive. The goal is to measure the internal filter, not the external environment. For older adolescents or girls, specifically ask about "Internal Distraction" (daydreaming/racing thoughts), as they often learn to camouflage physical reactions to external noises.',
    suggestedPrompts: [
      'When there are distractions around them - like noise, movement, conversations, or lots going on - can you describe what happens to their attention and how easy it is for them to get back on track?',
      'What kinds of distractions affect them most?',
    ],
    homeExamples: [
      'Frequently daydreams or quickly shifts focus to something more stimulating',
      'Easily distracted by background sights, sounds, or movement that others ignore',
      'Finds multitasking particularly challenging',
      'Finds it hard to get back to a task after being distracted',
      'Needs a quiet space or headphones to maintain focus',
    ],
    educationExamples: [
      'More distracted by classroom noise or activity than their peers',
      'Distracted by noise or activity inside as well as outside the classroom',
      'Goes off-task by chatting with peers',
      'Often seen to be looking outside and needs prompting to return to the task',
      'Works better one-to-one or in small groups due to reduced distraction',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A1i: {
    tooltip: 'This symptom is often linked to Parental Stress. Cross-referencing this with "Executive Function" scaffolding (e.g., use of calendars, alarms, or sticky notes) is vital. If they "remember" only because a parent manages their entire life, the symptom can be clinically present but camouflaged.',
    suggestedPrompts: [
      'In day-to-day life - like getting ready, remembering responsibilities, or moving between activities - can you describe how well they remember what they need to do without reminders or prompts?',
      'What kinds of things do they forget most often?',
    ],
    homeExamples: [
      'Frequently forgets where belongings were left, spending time searching for them',
      'Forgets to attend appointments, meetings, clubs or other planned activities',
      'Forgets to pass on important information or letters from school',
      'Leaves belongings and items that matter behind when visiting friends or relatives',
      'Needs reminders for routine self-care tasks (e.g., brushing teeth)',
    ],
    educationExamples: [
      'Regularly forgets their timetable or leaves essential items at home (books, kit)',
      'Forgets to collect all their belongings when moving between classrooms',
      'Forgets to hand homework in on time, even if it has been completed',
      'Forgets detentions, appointments or meetings despite knowing the consequences',
      'Teachers perceive them to be unreliable',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A2a: {
    tooltip: 'Consider the Gender and Age-Related Manifestation of this symptom. For girls and adolescents, ask about the internal feeling of restlessness that drives the fidgeting, as they may have learned to suppress larger movements to fit social norms. The symptom may present as subtle fidgeting, micro-movements, or internal agitation rather than overt squirming.',
    suggestedPrompts: [
      'Can you tell me about what their body (their hands, fingers, feet) is usually doing at any moment in time?',
      'What kinds of movements do you notice most?',
    ],
    homeExamples: [
      'Fidgets or is restless even when watching TV, at the dinner table, or in the car',
      'Fidgets even when engaged in an interesting task or when tired',
      'Often told to stop rocking on their chair, kicking their legs or fiddling with objects',
      'Fidgeting causes problems in restaurants, cinemas or places of worship',
      'Unable to remain seated in a chair in a relaxed manner',
    ],
    educationExamples: [
      'Fidgety / restless in class, whatever the lesson, topic or activity',
      'Fidgeting distracts peers or disrupts the classroom',
      'Shuffles in seat, kicks legs, rocks back on chair, fiddles with items, or doodles',
      'Perceived as clumsy because they often fall over or knock over items',
      'Able to control restlessness in class but appears stressed as a result',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A2b: {
    tooltip: 'This symptom is highly dependent on Social Expectation - consider Gender-Related Manifestation of this symptom as girls are more likely to suppress this to fit with social norms. Also look for "Contextual Friction": if they stay seated during a high-interest activity (e.g., playing a video game) but leave the seat every 2 minutes during a "boring" task, the symptom can still be positive. Focus on situations where remaining seated is clearly expected.',
    suggestedPrompts: [
      'Can you tell me what typically happens when they are asked or expected to stay in their seat for a period of time?',
      'Do they try to hide the urge to get up, and what happens when they do?',
    ],
    homeExamples: [
      'Struggles to stay seated, getting up multiple times even when sitting is important',
      'Has difficulty settling down',
      'Wanders around during meals, TV, or quiet activities, unable to remain seated',
      'Needs constant engagement to stay seated on public transport or long journeys',
      'Appears noticeably stressed due to difficulty of sitting still',
    ],
    educationExamples: [
      'Struggles to stay seated despite frequent reminders and redirection',
      'Leaves their seat to sit on the floor, move around, or roll about during floor work',
      'Constant movement disrupts peers and is disruptive to the classroom',
      'Finds excuses and opportunities to get up and move around (e.g., toilet trips)',
      'Unable to modify behaviour even when redirected or reprimanded',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A2c: {
    tooltip: 'Utilise an "Age and Gender Dependent Presentation" filter. For younger children, observe for "Gross Motor Excess" (running/climbing), but for girls and older children, observe for "Subjective Restlessness." The shift from objective behaviour to subjective feeling is a core part of the transition for adolescents, while girls are more likely to suppress this to fit social norms.',
    suggestedPrompts: [
      'Can you tell me about times when their need to move seems bigger or more difficult to control?',
      'How do they manage situations where they are expected to slow down, or relax?',
    ],
    homeExamples: [
      'Runs around or climbs on objects despite attempts to manage this behaviour',
      'Has damaged furniture or hurt themselves running or climbing',
      'Runs or climbs in areas where this is not permitted',
      'Engages in risky climbing or running, such as on roofs, cars, trees, streets, tracks',
      'Older children: channels the urge to run and climb into sporting activities',
    ],
    educationExamples: [
      'Appears restless and eager for break time, when they can engage in play',
      'Needs time to calm down when returning to the classroom after break or PE',
      'Reprimanded for running in corridors or moving unsafely around school',
      'Older children: seem more restless and fidgety rather than overtly active',
      'Needs extra supervision on class trips to stay with the group or walk safely',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A2d: {
    tooltip: 'It is important to distinguish between hyperactivity and sensory-seeking behaviour. Some children and adolescents may appear unusually loud, noisy, or energetic because they require increased sensory input or stimulation to feel regulated. Consider whether the behaviour reflects motor hyperactivity, sensory seeking, excitement regulation difficulties, or a combination of these. Cross-reference with a sensory profile where appropriate. Older children, adolescents, and girls may also suppress overt noisiness in structured settings but still describe an internal sense of excessive energy, difficulty "switching off," or needing constant stimulation.',
    suggestedPrompts: [
      'Can you describe what they are usually like during games, leisure activities, or quieter situations?',
      'How easy is it for them to stay calm, quiet, or settled when the situation calls for it?',
    ],
    homeExamples: [
      'Rarely engages in quiet play; described as loud, on the go, or overly active',
      'Struggles to play quietly, often getting up, running around, or making noise',
      'Struggles to moderate voice in museums, galleries or places of worship',
      'Disturbs others by talking throughout TV programmes or at the cinema',
      'Constantly asked to be quieter or to calm down',
    ],
    educationExamples: [
      'Talks or shouts when it\'s quiet time or during activities that require concentration',
      'Asks lots of questions, talks over others, and distracts their peers',
      'Reminders to be quiet and/or reprimands don\'t seem to help',
      'Given the choice, they avoid quiet activities, choosing noisy or boisterous ones',
      'Often reprimanded for chatting and disturbing their peers during individual work',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A2e: {
    tooltip: 'Consider "recovery" or "wind-down" patterns. Many children and adolescents are active at times, but in ADHD the activity level may appear unusually persistent or difficult to regulate across the day. Some children and adolescents appear to "reset" very quickly after physical activity and continue to seek movement even after opportunities for exercise or breaks.',
    suggestedPrompts: [
      'How would you describe their overall energy level across the day?',
      'Do they seem constantly on the go, restless, or driven to keep moving even when others are settling down?',
    ],
    homeExamples: [
      'Constantly on the go from morning to night, struggling to switch off',
      'Moves rapidly between activities, appearing like a "whirlwind" and unable to settle',
      'Needs additional supervision on public transport to stay seated and safe',
      'Parents/carers report feeling worn out by the child\'s activity level',
      'Reports wanting to relax but feeling unable to \'switch off\', even at bedtime',
    ],
    educationExamples: [
      'Seen as a whirlwind, running, climbing, and moving aimlessly between activities',
      'Seen as chaotic or always on the go, even during structured lessons',
      'At the end of the day, does not seem tired but instead irritable or overwhelmed',
      'Strongly favours break times and physical activities over classroom work',
      'Chooses physically active extracurriculars rather than less active pursuits',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A2f: {
    tooltip: 'It can be helpful to distinguish between socially reciprocal communication and difficulty regulating verbal output. In ADHD, excessive talking is often linked to impulsivity, rapid thought flow, excitement, or difficulty inhibiting speech, rather than solely a desire for social connection. Consider whether the child or young person can adjust the amount, timing, and pace of talking to match the situation and the listener’s engagement. Older adolescents, girls, and high-camouflaging individuals may suppress overt talking in structured settings but still describe an internal "mental chatter," pressure to speak, or rapid thought flow.',
    suggestedPrompts: [
      'How would you describe the amount and pace of their talking?',
      'Do they tend to talk more than other people, find it difficult to stop talking, or keep talking even when others are ready to move on?',
    ],
    homeExamples: [
      'Chatters constantly, even when they know they should be quiet',
      'Jumps from topic to topic with an endless stream of talk',
      'Dominates conversations with an endless narrative and struggles to stop talking',
      'Interrupts conversations of others, even when they are on the phone',
      'Parents/carers frequently remind them to be quiet or settle down',
    ],
    educationExamples: [
      'Chats to peers during lessons, even when asked to work quietly or during tests',
      'Does not respond to reminders or reprimands to stop talking',
      'Dominates class discussions and talks over others (even teachers)',
      'Gives tangential or overly long responses when answering questions',
      'So busy talking to the extent that their peers find it tiring',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A2g: {
    tooltip: 'Consider whether the behaviour reflects situational excitement/enthusiasm or a broader difficulty inhibiting responses and tolerating conversational delay. In ADHD, blurting often reflects urgency of responding, difficulty holding thoughts internally, or reduced inhibitory control across settings rather than excitement alone. Older adolescents and high-camouflaging individuals may suppress overt blurting in formal settings but still describe intense internal pressure to respond quickly, interrupt mentally, or finish others’ sentences internally.',
    suggestedPrompts: [
      'Can you tell me about situations where they respond, answer, or say things before they have fully thought things through?',
      'Can you give a recent example of a time when they responded quickly before finishing thinking?',
    ],
    homeExamples: [
      'Difficulty waiting for their turn to speak and interrupts or blurts things out',
      'Struggles with conversational turn-taking, needing to speak immediately',
      'They say what they think without thinking it through',
      'Seems impatient for others to finish speaking, interrupting mid-sentence',
      'Finishes other people\'s sentences for them',
    ],
    educationExamples: [
      'Frequently reminded or reprimanded for talking in lessons',
      'Dominates the class by frequently calling out or talking over others',
      'Difficulty waiting for their turn to speak and/or answer questions in class',
      'Older children: aware of it and tries to hold back, but can\'t',
      'Peers get irritated by it, which affects friendships',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A2h: {
    tooltip: 'It is helpful to differentiate between Physical Impatience and Emotional Dysregulation. Consider whether the difficulty waiting is significantly greater than expected for developmental age and context.',
    suggestedPrompts: [
      'Can you tell me about situations where they have to wait and what usually happens?',
      'Are there situations where they can wait perfectly well, and others where waiting becomes very hard?',
    ],
    homeExamples: [
      'Acts immediately without considering risks or consequences, leading to injuries',
      'Very impatient and becomes distressed or emotional when they have to wait',
      'Avoids queues as they become disruptive causing embarrassment waiting in line',
      'Parents/carers have had to leave shopping in supermarkets due to queues',
      'Struggles to wait even for enjoyable activities, such as rides at theme parks',
    ],
    educationExamples: [
      'Reprimanded for pushing, shoving, or queue-jumping during class activities',
      'Struggles to wait their turn when using equipment or participating in games',
      'Becomes distressed or agitated even for activities requiring a short wait',
      'Peers see them as self-centred for not waiting their turn',
      'Finds it difficult to remain engaged in an activity when there is a period of waiting',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
  A2i: {
    tooltip: 'It is helpful to distinguish between an impulse control issue and a social-reasoning deficit: is the intrusion due to a lack of understanding (Social Communication/ASD) or an inability to inhibit the impulse (ADHD)? ADHD-related interruption is more often driven by impaired inhibition and rapid internal urgency, whereas ASD-related interruption is more often associated with differences in social timing, perspective-taking, or conversational reciprocity. In practice, both awareness and inhibition should be explored rather than assumed.',
    suggestedPrompts: [
      'In social situations (at school, home, or with friends), can you describe what happens when they want to join a conversation or activity - how do they join in, and how do others react?',
      'Can you walk me through a recent example where they joined in or interrupted someone?',
    ],
    homeExamples: [
      'Interrupts private conversations, private spaces or activities of others',
      'They understand social boundaries but do not have the patience to manage them',
      'Uses other people\'s things without asking',
      'Seen as attention-seeking, and telling off makes little difference',
      'Act without thinking through the consequences of their behaviour',
    ],
    educationExamples: [
      'Interrupts teachers or peers, speaking out in lessons or assemblies',
      'Uses or takes equipment or belongings that do not belong to them without asking',
      'Takes over or redirects group activities, games or tasks without being invited',
      'Acts impulsively without considering impact, leading to conflict',
      'Has an opinion about everything and immediately expresses it',
    ],
    examplesHint: '',
    examplesFootnote: '',
  },
};
